import React, { useEffect, useState } from 'react';
import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';

export interface InlineNumberProps {
  value: number | null | undefined;
  /** called on blur / Enter when the value actually changed */
  onCommit: (value: number) => void;
  disabled?: boolean;
  /** shown (static, muted) when value is null/undefined */
  naText?: string;
  error?: boolean;
  formatter?: InputNumberProps['formatter'];
  parser?: InputNumberProps['parser'];
  min?: number;
  max?: number;
  step?: number;
  ariaLabel?: string;
  /** a non-editable unit shown outside the input (e.g. "%", "periods"). May be a
   *  function of the current (draft) value so units like day/days stay correct
   *  live while typing. */
  addonAfter?: React.ReactNode | ((value: number) => React.ReactNode);
  /** bordered (not ghost) — for form-row fields, esp. those with an addon */
  plain?: boolean;
  /** show the up/down steppers (antd reveals them on hover/focus only). Default true. */
  controls?: boolean;
}

/**
 * Seamless inline numeric cell — an always-mounted InputNumber styled to look
 * like static text until hovered/focused (the "ghost input" pattern). There is
 * no view↔edit mode swap, so toggling never reflows the layout (zero shift).
 * Commits on blur or Enter; Escape reverts to the last committed value.
 */
const InlineNumber: React.FC<InlineNumberProps> = ({
  value,
  onCommit,
  disabled,
  naText = 'NA',
  error,
  formatter,
  parser,
  min,
  max,
  step,
  ariaLabel,
  addonAfter,
  plain,
  controls = true,
}) => {
  const [draft, setDraft] = useState<number | null | undefined>(value);
  useEffect(() => setDraft(value), [value]);

  // A null/undefined value means "not applicable" for this row — render a
  // static placeholder (kept the same box metrics as the input).
  if (value === null || value === undefined) {
    return <span className="ie-num ie-num--na">{naText}</span>;
  }

  const commit = () => {
    if (draft === null || draft === undefined) {
      setDraft(value);
      return;
    }
    if (draft !== value) onCommit(draft);
  };

  // Resolve a function addon against the live (draft) value so units like
  // day/days or period/periods update as the user types.
  const resolvedAddon =
    typeof addonAfter === 'function'
      ? addonAfter(typeof draft === 'number' ? draft : value)
      : addonAfter;

  return (
    <span
      className={['ie-num', plain ? 'ie-num--plain' : '', error ? 'ie-num--error' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <InputNumber
        value={draft}
        disabled={disabled}
        controls={controls}
        size="small"
        formatter={formatter}
        parser={parser}
        min={min}
        max={max}
        step={step}
        addonAfter={resolvedAddon}
        aria-label={ariaLabel}
        onChange={v => setDraft(v as number | null)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setDraft(value);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    </span>
  );
};

export default InlineNumber;
